import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { MailIcon, PhoneIcon, MapPinIcon, SendIcon } from '../components/icons/Icons';
import { useSettings } from '../context/SettingsContext';
import api from '../services/api';
import './Contact.css';

const Contact = () => {
  const { settings } = useSettings();
  const statusRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await api.post('/contact', formData);
      setStatus({ type: 'success', message: response.data.message });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => statusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.errors?.[0]?.msg ||
                          'Failed to send message. Please try again.';
      setStatus({ type: 'error', message: errorMessage });
      setTimeout(() => statusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Fuwali Investments Zambia Limited</title>
        <meta name="description" content="Get in touch with Fuwali Investments Zambia Limited. We'd love to hear from you about partnership opportunities and inquiries." />
        <link rel="canonical" href="https://fuwalizambia.com/contact" />
        <meta property="og:title" content="Contact Us | Fuwali Investments Zambia Limited" />
        <meta property="og:description" content="Get in touch with Fuwali Investments Zambia Limited for partnership opportunities and inquiries." />
        <meta property="og:url" content="https://fuwalizambia.com/contact" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Page Hero */}
      <section className="page-hero" style={settings.hero_bg_contact ? { backgroundImage: `url(${settings.hero_bg_contact})` } : {}}>
        <div className="page-hero-overlay"></div>
        <div className="container">
          <span className="page-hero-label">Contact Us</span>
          <h1 className="page-hero-title">Get In Touch</h1>
          <p className="page-hero-subtitle">
            We'd love to hear from you. Reach out to discuss partnership opportunities or any inquiries.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info">
              <h2>Let's Connect</h2>
              <p className="contact-info-text">
                Whether you're interested in investment opportunities, partnerships, or have general inquiries, our team is here to help.
              </p>

              <div className="contact-details">
                <div className="contact-detail">
                  <div className="contact-detail-icon">
                    <MailIcon size={24} />
                  </div>
                  <div>
                    <h4>Email</h4>
                    <a href={`mailto:${settings.footer_email}`}>{settings.footer_email}</a>
                  </div>
                </div>

                <div className="contact-detail">
                  <div className="contact-detail-icon">
                    <PhoneIcon size={24} />
                  </div>
                  <div>
                    <h4>Phone</h4>
                    <a href={`tel:${settings.footer_phone}`}>{settings.footer_phone}</a>
                  </div>
                </div>

                <div className="contact-detail">
                  <div className="contact-detail-icon">
                    <MapPinIcon size={24} />
                  </div>
                  <div>
                    <h4>Address</h4>
                    <span>{settings.footer_address}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <form onSubmit={handleSubmit} className="contact-form">
                <h3>Send Us a Message</h3>

                {status.message && (
                  <div ref={statusRef} className={`alert alert-${status.type}`}>
                    {status.message}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      required
                      maxLength={255}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      maxLength={50}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject" className="form-label">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="form-input"
                      maxLength={255}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-input form-textarea"
                    required
                    maxLength={5000}
                    rows={6}
                  />
                </div>

                <button type="submit" className="btn btn-primary btn-submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message <SendIcon size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
